import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-latest-qr-2026': 'https://readdy.ai/api/search-image?query=Modern%20clean%20technology%20concept%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20QR%20code%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-latest-qr-2026&orientation=landscape',
  'article-scan-tutorial': 'https://readdy.ai/api/search-image?query=Modern%20technology%20education%20illustration%20with%20warm%20amber%20tones%2C%20smartphone%20scanning%20concept%2C%20minimalist%20design%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-scan-tutorial&orientation=landscape',
  'article-safety-guide': 'https://readdy.ai/api/search-image?query=Modern%20security%20shield%20concept%20illustration%20with%20warm%20coral%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20protection%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-safety-guide&orientation=landscape',
  'article-invalid-solution': 'https://readdy.ai/api/search-image?query=Modern%20problem%20solving%20illustration%20with%20warm%20amber%20and%20gray%20tones%2C%20minimalist%20design%2C%20abstract%20connection%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-invalid-solution&orientation=landscape',
  'article-mobile-guide': 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20mobile%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-mobile-guide&orientation=landscape',
  'article-registration': 'https://readdy.ai/api/search-image?query=Modern%20signup%20registration%20illustration%20with%20warm%20amber%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20user%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-registration&orientation=landscape',
  'article-faq-summary': 'https://readdy.ai/api/search-image?query=Modern%20FAQ%20help%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20question%20mark%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-faq-summary&orientation=landscape',
  'article-vpn-guide': 'https://readdy.ai/api/search-image?query=Modern%20network%20security%20illustration%20with%20warm%20amber%20and%20golden%20tones%2C%20minimalist%20design%2C%20abstract%20connection%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-vpn-guide&orientation=landscape',
  'article-safety-tips': 'https://readdy.ai/api/search-image?query=Modern%20safety%20warning%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20alert%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-safety-tips&orientation=landscape',
  'article-features': 'https://readdy.ai/api/search-image?query=Modern%20community%20platform%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20abstract%20features%20concept%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20digital%20theme%2C%20no%20text%2C%20no%20people%2C%20elegant%20composition&width=800&height=500&seq=article-features&orientation=landscape',
};

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
});

function getCategorySchema(catSlug: string) {
  const catName = categoryNameMap[catSlug] || '分类';
  const filteredArticles = catSlug === 'all' ? articles : articles.filter((a) => a.category === catSlug);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${catName} - 草榴二维码指南`,
        description: `草榴二维码${catName}相关文章合集，提供最新的${catName}信息与指南`,
        url: `https://caoliu-qrcode.example.com/category/${catSlug}`,
        isPartOf: { '@id': 'https://caoliu-qrcode.example.com/#website' },
        inLanguage: 'zh-CN',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: filteredArticles.map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `https://caoliu-qrcode.example.com/article/${article.slug}`,
            name: article.title,
          })),
        },
      },
    ],
  };
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const categoryTabs = [
    { slug: 'all', name: t('category.allArticles') },
    ...categories.map((c) => ({ slug: c.slug, name: c.name })),
  ];

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const catName = activeCategory === 'all' ? t('category.allArticles') : (categoryNameMap[activeCategory] || activeCategory);

  const seoTitle = `${catName} - 草榴二维码指南`;
  const seoDesc = `草榴二维码${catName}相关文章合集，提供最新的${catName}信息与使用指南，帮助您快速掌握草榴社区访问技巧。`;
  const seoKeywords = `草榴二维码,${catName},草榴社区二维码,草榴入口二维码`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`/category/${activeCategory}`}
        schemaData={getCategorySchema(activeCategory)}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {t('category.title')}
            </h1>
            <p className="text-sm text-foreground-600">
              {t('category.subtitle')}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categoryTabs.map((tab) => (
              <Link
                key={tab.slug}
                to={`/category/${tab.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === tab.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-foreground-800'
                }`}
              >
                {tab.name}
              </Link>
            ))}
          </div>

          <p className="text-sm text-foreground-500 mb-6">
            {t('category.articlesFound', { count: filteredArticles.length })}
          </p>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                <i className="ri-inbox-line text-4xl"></i>
              </div>
              <p className="text-foreground-500">{t('category.noArticles')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={articleImages[article.imageSeq]}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm rounded text-xs font-medium text-foreground-700">
                        {categoryNameMap[article.category] || article.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="text-base font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 mb-4 line-clamp-2 leading-relaxed flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500 mt-auto pt-4 border-t border-background-200/70">
                      <span>{article.publishedAt}</span>
                      <span>{article.readTime} {t('home.articles.readTime')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}